import { Fab, SwipeableDrawer } from '@mui/material';
import styled from 'styled-components';
import { Icon } from 'components/common/icon/icon';
import { YourPlan } from './your-plan';
import { OrderSummary } from './order-summary';
import { useState } from 'react';
import { FormikProps } from 'formik';
import { JoinFormType, PackagePrices } from 'types/join';

const FloatingButton = styled(Fab)`
  width: 100px;
  position: fixed;
  top: 110px;
  right: 150px;
  z-index: 1000;
  display: none;
  background-color: ${props => props.theme.colors.siteBlue} !important;
  color: ${props => props.theme.colors.white} !important;
  padding: 0 5px !important;
  height: 38px !important;
  border-radius: 24px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  font-family: ${props => props.theme.fonts.montserratSemiBold} !important;
  font-size: 16px !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;

  &:hover {
    background-color: ${props => props.theme.colors.hoverBackgroundSiteBlue} !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  }

  @media (max-width: 999px) {
    display: flex;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  font-size: 13px;
  align-items: center;
  gap: 4px;
`;

const PlanBox = styled.div<{ planBoxWidth: number }>`
  width: ${props => props.planBoxWidth}px;
  min-width: 30%;
  margin-top: 30px;
  background-color: ${props => props.theme.colors.white};
  padding: 50px;
  position: relative;

  @media (max-width: 999px) {
    display: none;
  }
`;

const StickyBox = styled.div<{ planBoxWidth: number }>`
  position: sticky;
  width: ${props => props.planBoxWidth - 100}px;
  margin: auto;

  @media (max-width: 999px) {
    width: 100%;
    position: relative;
  }
`;

const MobileDrawer = styled(SwipeableDrawer)`
  & .MuiDrawer-paper {
    width: 100%;
    max-width: 400px;
    padding: 20px;
    box-sizing: border-box;
    z-index: 1200 !important;
  }
`;

const CloseDrawerButton = styled.button`
  position: absolute;
  top: 12px;
  left: 12px;
  background: transparent;
  border: none;
  z-index: 2;
  display: none;
  padding: 0;
  cursor: pointer;
  @media (max-width: 999px) {
    display: block;
  }
`;

interface YourPlanContainerProps {
  planBoxWidth: number;
  setSelectedAddons: (addons: Array<PackagePrices | undefined>) => void;
  setSelectedPlan: (plan: PackagePrices | undefined) => void;
  selectedPlan: PackagePrices | undefined;
  selectedBilledOption: string;
  setSelctedBilledOption: (option: string) => void;
  isYourPlanActive: boolean;
  setIsYourPlanActive: (active: boolean) => void;
  setNumConfirmClicked: (num: number) => void;
  form: FormikProps<JoinFormType>;
  isPriceList: boolean;
  selectedAddons: Array<PackagePrices | undefined>;
  onPromoCodeApplied: (promoCodeId: string) => void;
  setFinalTotalPrice: (total: number) => void;
  finalTotalPrice: number;
}

export const YourPlanContainer = ({
  planBoxWidth,
  setSelectedAddons,
  setSelectedPlan,
  selectedPlan,
  selectedBilledOption,
  setSelctedBilledOption,
  isYourPlanActive,
  setIsYourPlanActive,
  setNumConfirmClicked,
  form,
  isPriceList,
  selectedAddons,
  onPromoCodeApplied,
  setFinalTotalPrice,
  finalTotalPrice
}: YourPlanContainerProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <>
      {window.innerWidth > 999 && (
        <PlanBox planBoxWidth={planBoxWidth}>
          <StickyBox planBoxWidth={planBoxWidth}>
            <YourPlan
              setSelectedAddons={setSelectedAddons}
              setSelectedPlan={setSelectedPlan}
              selectedPlan={selectedPlan}
              selectedBilledOption={selectedBilledOption}
              setSelctedBilledOption={setSelctedBilledOption}
              isActive={isYourPlanActive}
              setIsActive={setIsYourPlanActive}
              setNumConfirmClicked={setNumConfirmClicked}
              form={form}
              isPriceList={isPriceList}
              setDrawerOpen={setIsDrawerOpen}
              isDrawerOpen={isDrawerOpen}
            />
            <OrderSummary
              selectedPlan={selectedPlan}
              selectedAddons={selectedAddons}
              userCountry={form.values.companyCountry ?? ''}
              setFinalTotalPrice={setFinalTotalPrice}
              finalTotalPrice={finalTotalPrice}
              isActive={isYourPlanActive}
            />
          </StickyBox>
        </PlanBox>
      )}
      {window.innerWidth <= 999 && (
        <>
          <FloatingButton
            color="primary"
            aria-label="view plan"
            onClick={() => setIsDrawerOpen(true)}
          >
            <ButtonContent>Your Plan</ButtonContent>
          </FloatingButton>
          <MobileDrawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onOpen={() => setIsDrawerOpen(true)}
            disableSwipeToOpen={false}
            swipeAreaWidth={20}
          >
            <CloseDrawerButton onClick={() => setIsDrawerOpen(false)} aria-label="Close sidebar">
              <Icon name="x" style={{ fontSize: 28, color: '#222' }} />
            </CloseDrawerButton>
            <YourPlan
              setSelectedAddons={setSelectedAddons}
              setSelectedPlan={setSelectedPlan}
              selectedPlan={selectedPlan}
              selectedBilledOption={selectedBilledOption}
              setSelctedBilledOption={setSelctedBilledOption}
              isActive={isYourPlanActive}
              setIsActive={setIsYourPlanActive}
              setNumConfirmClicked={setNumConfirmClicked}
              form={form}
              isPriceList={isPriceList}
              setDrawerOpen={setIsDrawerOpen}
              isDrawerOpen={isDrawerOpen}
            />
            <OrderSummary
              selectedPlan={selectedPlan}
              selectedAddons={selectedAddons}
              userCountry={form.values.companyCountry ?? ''}
              setFinalTotalPrice={setFinalTotalPrice}
              finalTotalPrice={finalTotalPrice}
              isActive={isYourPlanActive}
            />
          </MobileDrawer>
        </>
      )}
    </>
  );
};
